import React, { createContext, useCallback, useRef, useState } from "react"
import Cookies from "js-cookie"
import { get, post, put } from "utils/apiClient"

import { SCOPE_QD_REF, SCOPE_RECENT } from "./constants"

/**
 * API contract this page is written against. One router entity ("llm-instr"), disambiguated
 * the way cp_ui_crs_management's router already does it (METHOD_MAP: POST->create, GET->get,
 * PUT->update):
 *
 *   GET  crs/llm-instr?mail_tracker=<qd ref>                       -> resolve
 *   GET  crs/llm-instr?mail_tracker=<t>&message_id=<m>             -> one reply's body
 *   GET  crs/llm-instr?job_id=<id>                                 -> job poll
 *   POST crs/llm-instr   {tenant_id,m_loc_id,draft_instr,scope,..} -> {job_id}
 *   PUT  crs/llm-instr   {tenant_id,m_loc_id,addnl_llm_instr,...}  -> saved
 *
 * A QD Ref is the only way in. Both tables carrying a mail_tracker carry tenant_id and
 * m_loc_id beside it, so the tracker resolves the location on its own.
 */
const LlmInstrContext = createContext()

const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 10 * 60 * 1000

function Provider({ children }) {
  const isAdmin = Cookies.get("role") === "admin"

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [resolved, setResolved] = useState(null) // {response_context, instruction, samples}

  const [draft, setDraft] = useState("")

  const [job, setJob] = useState(null) // {job_id, status, result:{rows,counts}}
  const [previewing, setPreviewing] = useState(false)
  // Which button is in flight / produced the current job, so the results panel can say what
  // it validated against rather than leaving the reader to infer it from the row count.
  const [scope, setScope] = useState(null)
  // The draft text the completed preview was actually run against. Save unlocks only while
  // this equals the current draft -- that single rule is what stops anyone saving untested
  // text, so it is held here rather than inferred from the job payload.
  const [previewedDraft, setPreviewedDraft] = useState(null)

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(null)

  const pollTimer = useRef(null)

  const clearPreview = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
    setJob(null)
    setPreviewing(false)
    setPreviewedDraft(null)
    setScope(null)
  }, [])

  const reset = useCallback(() => {
    clearPreview()
    setResolved(null)
    setDraft("")
    setError("")
    setSaved(null)
  }, [clearPreview])

  // ----------------------------------------------------------------- resolve

  const resolveByTracker = useCallback(
    async mailTracker => {
      setLoading(true)
      setError("")
      setSaved(null)
      clearPreview()
      setResolved(null) // clear first so switching tracker cannot flash the previous one
      try {
        const data = await get(
          `crs/llm-instr?mail_tracker=${encodeURIComponent(mailTracker)}`)
        setResolved(data)
        setDraft(data?.instruction?.addnl_llm_instr || "")
        return data
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setLoading(false)
      }
    },
    [clearPreview]
  )

  /** One reply's full text, on demand. Bodies are tens of KB each and both grids together
   *  list twenty of them, so they are never shipped with the listings. */
  const fetchMessage = useCallback(async (mailTracker, messageId) => {
    const qs = new URLSearchParams({ mail_tracker: mailTracker, message_id: messageId })
    return get(`crs/llm-instr?${qs.toString()}`)
  }, [])

  // ----------------------------------------------------------------- preview

  const pollJob = useCallback(
    (jobId, startedAt, draftAtStart) => {
      pollTimer.current = setTimeout(async () => {
        try {
          const data = await get(`crs/llm-instr?job_id=${encodeURIComponent(jobId)}`)
          setJob(data)
          if (data.status === "DONE") {
            setPreviewing(false)
            setPreviewedDraft(draftAtStart)
            return
          }
          if (data.status === "FAILED" || data.status === "CANCELLED") {
            setPreviewing(false)
            setError(data.error || `Preview ${data.status.toLowerCase()}`)
            return
          }
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            setPreviewing(false)
            setError("Preview timed out. The job may still be running - reload to pick it up.")
            return
          }
          pollJob(jobId, startedAt, draftAtStart)
        } catch (err) {
          setPreviewing(false)
          setError(err.message)
        }
      }, POLL_INTERVAL_MS)
    },
    []
  )

  const runPreview = useCallback(
    async runScope => {
      if (!resolved?.response_context) return
      setError("")
      setPreviewing(true)
      setJob(null)
      setPreviewedDraft(null)
      setScope(runScope)
      const draftAtStart = draft
      try {
        const data = await post("crs/llm-instr", {
          tenant_id: resolved.response_context.tenant_id,
          m_loc_id: resolved.response_context.m_loc_id,
          draft_instr: draftAtStart,
          scope: runScope,
          trigger_mail_tracker: resolved.response_context.mail_tracker,
        })
        setJob({ job_id: data.job_id, status: data.status || "QUEUED" })
        pollJob(data.job_id, Date.now(), draftAtStart)
      } catch (err) {
        setPreviewing(false)
        setScope(null)
        setError(err.message)
      }
    },
    [resolved, draft, pollJob]
  )

  const validateQdRef = useCallback(() => runPreview(SCOPE_QD_REF), [runPreview])
  const validateRecent = useCallback(() => runPreview(SCOPE_RECENT), [runPreview])

  const cancelPreview = useCallback(() => {
    clearPreview()
  }, [clearPreview])

  // -------------------------------------------------------------------- save

  const save = useCallback(
    async () => {
      if (!resolved?.response_context) return null
      setSaving(true)
      setError("")
      try {
        const data = await put("crs/llm-instr", {
          tenant_id: resolved.response_context.tenant_id,
          m_loc_id: resolved.response_context.m_loc_id,
          addnl_llm_instr: draft,
          job_id: job?.job_id || null,
        })
        setSaved({ message: "Instruction saved", data })
        // Re-resolve so the provenance badge and counter reflect what is now stored.
        await resolveByTracker(resolved.response_context.mail_tracker)
        return data
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setSaving(false)
      }
    },
    [resolved, draft, job, resolveByTracker]
  )

  // --------------------------------------------------------------- derived

  const rows = job?.result?.rows || []
  // A row counts as "changed" for the Δ column if any of the 17 consolidated fields moved,
  // but only a MATERIAL change (status / appointment_status / confidence / confirmed times /
  // confirmation_code / is_automated_message) demands a reviewer's verdict. Judgement fields
  // like requesting_confirmation flip between runs on identical input, and making someone
  // justify that noise just teaches them to click through the gate.
  const materialOf = r => r.material_changed_fields || r.changed_fields || []
  const changedRows = rows.filter(r => (r.changed_fields || []).length > 0)
  const materialRows = rows.filter(r => materialOf(r).length > 0)

  const previewMatchesDraft = previewedDraft !== null && previewedDraft === draft
  // Save requires a preview against THIS exact draft -- that rule stops anyone saving
  // untested text and is the one worth keeping. Grading is NOT required: it is optional
  // annotation that rides along to the audit record. Demanding it before every save was
  // friction with no payoff, and a gate people click through teaches them to ignore it.
  const canSave = !!resolved?.response_context && previewMatchesDraft && !previewing && !saving

  const value = {
    isAdmin,
    loading,
    error,
    setError,
    resolved,
    reset,

    draft,
    setDraft,

    resolveByTracker,
    fetchMessage,

    job,
    scope,
    rows,
    changedRows,
    materialRows,
    materialOf,
    previewing,
    previewMatchesDraft,
    validateQdRef,
    validateRecent,
    cancelPreview,

    canSave,
    saving,
    saved,
    save,
  }

  return <LlmInstrContext.Provider value={value}>{children}</LlmInstrContext.Provider>
}

export { Provider }
export default LlmInstrContext
